import { useTimeout } from '@mantine/hooks'
import { useSetAtom } from 'jotai'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { track } from 'src/utils/tracker'
import { onboardingFirstPromptAtom } from '../../states'
import type { OnboardingCharacterInfo } from '../helper'
import { getOnboardingFirstPrompt } from '../helper'
import { NewUserOnboardingContext } from '../NewUserOnboardingContext'
import { useNewUserOnboardingFormContext } from '../newUserOnboardingForm'
import { NewUserOnboardingFormStep } from '../NewUserOnboardingFormStep'

const TYPING_DURATION_MS = 3000
const POST_REVEAL_PAUSE_MS = 2000

function findCharacterByTackId(
  selectedTackId: string | undefined,
  themes: Record<string, OnboardingCharacterInfo[]>,
): OnboardingCharacterInfo | undefined {
  if (!selectedTackId) return undefined
  for (const characterList of Object.values(themes)) {
    const found = characterList.find(c => c.tackId === selectedTackId)
    if (found) return found
  }
  return undefined
}

export function NewUserFirstPromptRevealStep() {
  const { t, i18n } = useTranslation(['user'])
  const { goToNextStepWithAction, onboardingDataRes } = useContext(
    NewUserOnboardingContext,
  )
  const form = useNewUserOnboardingFormContext()
  const setFirstPrompt = useSetAtom(onboardingFirstPromptAtom)

  const prompt = useMemo(() => {
    const { characters, promptStyle } = form.getValues()
    if (!promptStyle) return ''
    const themes = onboardingDataRes?.onboardingData?.themes ?? {}
    const character = findCharacterByTackId(characters?.at(0), themes)
    if (character) {
      return getOnboardingFirstPrompt(character, promptStyle, i18n)
    }
    return promptStyle === 'precise-tags'
      ? '1girl, anime, masterpiece, best quality'
      : 'Anime illustration of a girl, masterpiece, best quality'
  }, [form.getValues, onboardingDataRes, i18n])

  // Token-end offsets into the prompt, so the typewriter reveals by slicing the
  // original string (preserving its commas/spacing). Intl.Segmenter (word
  // granularity) is used so Japanese — which has no spaces — still animates in
  // segments instead of popping in all at once; older engines fall back to
  // per-code-point.
  const tokenEndOffsets = useMemo(() => {
    const offsets: number[] = []
    if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
      const segmenter = new Intl.Segmenter(i18n.language, {
        granularity: 'word',
      })
      for (const seg of segmenter.segment(prompt)) {
        offsets.push(seg.index + seg.segment.length)
      }
    } else {
      let offset = 0
      for (const ch of prompt) {
        offset += ch.length
        offsets.push(offset)
      }
    }
    return offsets
  }, [prompt, i18n.language])
  const tokenCount = tokenEndOffsets.length
  const [tokensShown, setTokensShown] = useState(0)
  const visibleText = useMemo(
    () =>
      tokensShown > 0 ? prompt.slice(0, tokenEndOffsets[tokensShown - 1]) : '',
    [prompt, tokenEndOffsets, tokensShown],
  )

  useEffect(() => {
    if (tokenCount === 0) return
    const interval = TYPING_DURATION_MS / tokenCount
    let i = 0
    const handle = window.setInterval(() => {
      i += 1
      setTokensShown(i)
      if (i >= tokenCount) window.clearInterval(handle)
    }, interval)
    return () => window.clearInterval(handle)
  }, [tokenCount])

  const allShown = tokenCount > 0 && tokensShown >= tokenCount
  const { start: startCloseTimer } = useTimeout(() => {
    if (prompt) {
      setFirstPrompt({ prompt, tooltipPending: true })
    }
    goToNextStepWithAction()
  }, POST_REVEAL_PAUSE_MS)
  useEffect(() => {
    if (allShown) startCloseTimer()
  }, [allShown, startCloseTimer])

  useEffect(() => {
    if (!prompt) return
    track('onboarding_first_prompt_show', {
      promptStyle: form.getValues().promptStyle,
      source: 'prebaked',
      length: prompt.length,
    })
  }, [prompt])

  return (
    <NewUserOnboardingFormStep
      disallowBack
      overrideStepperContent={<></>}
      overridePaginatorContent={<></>}
    >
      <div className="h-full flex flex-col items-center justify-center px-6">
        <h2 className="text-center text-xl font-bold mb-8">
          {t('user:onboarding.first-prompt.title')}
        </h2>
        <p className="italic text-center text-lg text-neutral-700 dark:text-neutral-300 min-h-[120px]">
          “{visibleText}”
        </p>
      </div>
    </NewUserOnboardingFormStep>
  )
}
